import {
  ExpiryType,
  InventoryManagementMethod,
  LanguageType,
  NbSelectItemOption,
  OrderType,
  PeriodType,
  SessionTimeout,
  ThemeMode,
  TradingSession,
  TriggerState,
  useOptionsCommon,
} from '@eih-netbroker/common';
import { useMemo } from 'react';
import FlagHu from '../../assets/svg/market-flags/flag_hu.svg';
import FlagUs from '../../assets/svg/market-flags/flag_us_circle.svg';
import { useLocale } from '../../context/NbLocalizationContext';

export const useOptions = () => {
  const { translation } = useLocale();
  const {
    getAllOrderTypeOptions,
    getInventoryManagementMethodOptions,
    getTradingSessionOptions,
    getOrderTypeOptions,
    getExpiryTypeOptions,
    getAllExpiryTypeOptions,
    getSelectAllOption,
    getBetTradingSessionOptions,
    getUsaTradingSessionOptions,
    getXetradingSessionOptions,
    getTriggerStateOptions,
    getLanguageOptions,
    getSessionTimeoutOptions,
    getDisplayModeOptions,
    getPeriodOptions,
  } = useOptionsCommon({ FlagHu, FlagUs });

  const orderTypeOptions = useMemo<NbSelectItemOption<OrderType>[]>(
    () => getAllOrderTypeOptions(),
    [getAllOrderTypeOptions],
  );

  const inventoryManagementMethodOptions = useMemo<NbSelectItemOption<InventoryManagementMethod>[]>(
    () => getInventoryManagementMethodOptions(translation.inventoryManagementMethodOptions),
    [getInventoryManagementMethodOptions, translation.inventoryManagementMethodOptions],
  );

  const expiryTypeOptions = useMemo<NbSelectItemOption<ExpiryType>[]>(
    () => getAllExpiryTypeOptions(),
    [getAllExpiryTypeOptions],
  );

  const selectAllOption = useMemo<NbSelectItemOption<string>>(
    () => getSelectAllOption(translation.general.emptySelectAll),
    [getSelectAllOption, translation.general.emptySelectAll],
  );

  const betTradingSessionOptions = useMemo<NbSelectItemOption<TradingSession>[]>(
    () => getBetTradingSessionOptions(),
    [getBetTradingSessionOptions],
  );

  const usaTradingSessionOptions = useMemo<NbSelectItemOption<TradingSession>[]>(
    () => getUsaTradingSessionOptions(),
    [getUsaTradingSessionOptions],
  );

  const xetradingSessionOptions = useMemo<NbSelectItemOption<TradingSession>[]>(
    () => getXetradingSessionOptions(),
    [getXetradingSessionOptions],
  );

  const triggerStateOptions = useMemo<NbSelectItemOption<TriggerState>[]>(
    () => getTriggerStateOptions(translation.priceAlerts.triggerState),
    [getTriggerStateOptions, translation.priceAlerts.triggerState],
  );

  const languageOptions = useMemo<NbSelectItemOption<LanguageType>[]>(
    () => getLanguageOptions(translation.general.languageOptions),
    [getLanguageOptions, translation.general.languageOptions],
  );

  const sessionTimeoutOptions = useMemo<NbSelectItemOption<SessionTimeout>[]>(
    () => getSessionTimeoutOptions(translation.settings.siteSettings.sessionTimeoutOptions),
    [getSessionTimeoutOptions, translation.settings.siteSettings.sessionTimeoutOptions],
  );

  const displayModeOptions = useMemo<NbSelectItemOption<ThemeMode>[]>(
    () => getDisplayModeOptions(translation.settings.siteSettings.displayModeOptions),
    [getDisplayModeOptions, translation.settings.siteSettings.displayModeOptions],
  );

  const periodOptions = useMemo<NbSelectItemOption<PeriodType>[]>(
    () => getPeriodOptions(translation.orderLog.periodOptions),
    [getPeriodOptions, translation.orderLog.periodOptions],
  );

  return {
    orderTypeOptions,
    inventoryManagementMethodOptions,
    getTradingSessionOptions,
    getOrderTypeOptions,
    getExpiryTypeOptions,
    expiryTypeOptions,
    selectAllOption,
    betTradingSessionOptions,
    usaTradingSessionOptions,
    xetradingSessionOptions,
    triggerStateOptions,
    languageOptions,
    sessionTimeoutOptions,
    displayModeOptions,
    periodOptions,
  };
};
